import { useState, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

export interface APIEntity {
  entityRef: string;
  name: string;
  type: string;
  system: string;
  description: string;
}

export const useAPIData = (selectedRepositorySystem: string | undefined) => {
  const catalogApi = useApi(catalogApiRef);
  const [apis, setApis] = useState<APIEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRepositorySystem, setLastRepositorySystem] = useState<string>('');

  useEffect(() => {
    const fetchApis = async () => {
      if (!selectedRepositorySystem) {
        setApis([]);
        setLastRepositorySystem('');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if we need to fetch new data
        if (
          selectedRepositorySystem === lastRepositorySystem &&
          apis.length > 0
        ) {
          return;
        }

        // Fetch APIs with matching system
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'API',
            'spec.system': `system:default/${selectedRepositorySystem}`,
          },
        });

        const apiEntities: APIEntity[] = response.items.map(
          (entity: Entity) => ({
            entityRef: stringifyEntityRef(entity),
            name: entity.metadata.name,
            type: (entity.spec?.type as string) || 'unknown',
            system: (entity.spec?.system as string) || 'unknown',
            description:
              entity.metadata.description || 'No description available',
          }),
        );

        setApis(apiEntities);
        setLastRepositorySystem(selectedRepositorySystem);
      } catch (err) {
        setError('Failed to fetch APIs');
      } finally {
        setLoading(false);
      }
    };

    fetchApis();
  }, [selectedRepositorySystem, catalogApi, lastRepositorySystem, apis.length]);

  return { apis, loading, error };
};
